import { faEnvelope } from "@fortawesome/pro-light-svg-icons";
import { faThumbsUp } from "@fortawesome/pro-solid-svg-icons";
import { Location } from "history";
import React from "react";

import { bazaarMyBazaarUrl, bazaarSellerBazaarUrl } from "@/mk2/apps/bazaar/urls";
import ProfileAdminTools from "@/mk2/apps/blogs/components/ProfileAdminTools";
import { ProfileDetailData } from "@/mk2/apps/blogs/containers/ProfileDetail/ProfileDetail.reducers";
import { blogsProfileMoreUrl, blogsProfileUrl } from "@/mk2/apps/blogs/urls";
import { groupsGroupUrl } from "@/mk2/apps/groups/urls";
import { mailComposeUrl } from "@/mk2/apps/mail/urls";
import { SexType } from "@/mk2/apps/settings/schemas";
import { settingsProfileUrl } from "@/mk2/apps/settings/urls";
import { usersDirectoryUrl } from "@/mk2/apps/users/urls";
import { getGenericAvatar } from "@/mk2/apps/users/utils";
import { AvatarImg } from "@/mk2/components/AvatarImg";
import Badge, { BadgeType } from "@/mk2/components/Badge";
import { Btn, BtnLayout, BtnType } from "@/mk2/components/Btn";
import { ExpandableClamp } from "@/mk2/components/ExpandableClamp";
import { ImgVarHeight } from "@/mk2/components/ImgVarHeight";
import { Interpolate } from "@/mk2/components/Interpolate";
import { Link } from "@/mk2/components/Link";
import { LoadingPlaceholder } from "@/mk2/components/LoadingPlaceholder";
import { url } from "@/mk2/helpers/urls";
import { UserEntity } from "@/mk2/schemas";
import { interpolate } from "@/mk2/services/i18n";
import {
    ABOUT_ME_CHANGE_PROFILE,
    ABOUT_ME_FOLLOWERS_count,
    ABOUT_ME_FOLLOWING_count,
    ABOUT_ME_GROUPS_count,
    ABOUT_ME_MORE_ABOUT_ME,
    ABOUT_ME_PRODUCTS_count,
    BEFORE_BIRTH_1,
    BEFORE_BIRTH_2,
    CHANGE,
    PROFILE_REGISTERED_ON_date,
    PROFILE_SUMMARY__IN_num_PREGNANCY_WEEK,
    PROFILE_SUMMARY__name_IS_in_num_pregnancy_week,
    PROFILE_VISIT_GROUP_group,
    SEND_INTERNAL_POST,
    USER_LIKE,
    USER_UNLIKE,
    number_DAYS,
    number_YEARS,
} from "@/mk/autogenerated/translation";
import { canManageBlogs, isAuthenticated, isAuthenticatedAs } from "@/mk/bazaar/common/userUtils";
import { capFirst } from "@/mk/common/text";
import { photoUrl } from "@/mk/photo/photoUrl";

import styles from "./AboutMe.mscss";

interface OwnProps {
    isMobile: boolean;
    location: Location;
    moreAboutMeHasContent?: boolean;
    profileDetailData: Pick<
        ProfileDetailData,
        | "ambassadorBrandGroupId"
        | "ambassadorBrandLogo"
        | "ambassadorBrandName"
        | "pregnancyDaysCounter"
        | "pregnancyWeekNumber"
        | "user"
        | "userAboutMeText"
        | "userAge"
        | "userAvatarPhoto"
        | "userIsInactive"
        | "userIsLiked"
        | "userOnlineStatus"
        | "userRegisteredOn"
        | "userResidence"
        | "userResidenceCountyId"
    > & {
        following: Pick<ProfileDetailData["following"], "count">;
        followers: Pick<ProfileDetailData["followers"], "count">;
        memberOfGroups: Pick<ProfileDetailData["memberOfGroups"], "count">;
        userStats: Pick<ProfileDetailData["userStats"], "marketProductsCount">;
    };
    requestUser: UserEntity;
    requestUserPermissions: string[];
    onDeleteAvatarByAdmin(username: string);
    onSetLikeUser(username: string, like: boolean);
}

export type Props = OwnProps;

export default class AboutMe extends React.Component<Props> {
    public render() {
        const {
            isMobile,
            onDeleteAvatarByAdmin,
            profileDetailData,
            requestUser,
            requestUserPermissions,
            moreAboutMeHasContent,
        } = this.props;

        const {
            ambassadorBrandGroupId,
            ambassadorBrandLogo,
            ambassadorBrandName,
            pregnancyDaysCounter,
            pregnancyWeekNumber,
            user,
            userAboutMeText,
            userAge,
            userAvatarPhoto,
            userIsInactive,
            userIsLiked,
            userOnlineStatus,
            userRegisteredOn,
            userResidence,
            userResidenceCountyId,
            userStats,
            following,
            followers,
            memberOfGroups,
        } = profileDetailData;

        const isAdmin = canManageBlogs(requestUserPermissions);
        const isOwner = isAuthenticatedAs(requestUser, user);
        const isBlogsAdmin = canManageBlogs(requestUserPermissions);

        const avatar = (
            <div className={styles.AboutMe__avatar}>
                <AvatarImg
                    className={styles.AboutMe__avatar__image}
                    href={url(blogsProfileUrl, { username: user.username })}
                    user={user}
                    // Note: userAvatarPhoto is outside UserEntity since UserEntity is used in mass and avatar
                    //       as PhotoEntity we need just in the Profile right now
                    // largestAvailablePhotoVersionOfPost(, '')
                    userAvatarBig={
                        userAvatarPhoto
                            ? photoUrl(userAvatarPhoto, "s1600x1600")
                            : getGenericAvatar(
                                  150,
                                  user.gender !== "M" ? SexType.Girl : SexType.Boy,
                              )
                    }
                    size={isMobile ? 150 : 200}
                />
            </div>
        );

        const ambassadorPanel = (
            <div className={styles.AboutMe__ambassadorBanner}>
                <Badge
                    type={
                        user.isCommercialAmbassador
                            ? BadgeType.CommercialAmbassador
                            : BadgeType.Ambassador
                    }
                    className={styles.AboutMe__ambassadorBanner__badge}
                />
                <ImgVarHeight
                    photo={ambassadorBrandLogo}
                    width={270}
                    photoVersion={"s270x100"}
                    className={styles.AboutMe__ambassadorBanner__image}
                />
                {ambassadorBrandGroupId && (
                    <Link to={url(groupsGroupUrl, { groupId: ambassadorBrandGroupId })}>
                        {interpolate(PROFILE_VISIT_GROUP_group, { group: ambassadorBrandName })}
                    </Link>
                )}
            </div>
        );

        const likeUser = (
            <div className={styles.AboutMe__like}>
                {!userIsLiked ? (
                    <Btn
                        layout={BtnLayout.IconLabelCount}
                        type={BtnType.Blue}
                        icon={faThumbsUp}
                        label={<b>{USER_LIKE}</b>}
                        onClick={this.handleLikeUser}
                    />
                ) : (
                    <Btn
                        layout={BtnLayout.IconLabelCount}
                        type={BtnType.BlueOutline}
                        label={<b>{USER_UNLIKE}</b>}
                        onClick={this.handleUnLikeUser}
                    />
                )}
            </div>
        );

        if (!isAuthenticated(requestUser)) {
            return (
                <div className={styles.AboutMe}>
                    {avatar}
                    <div className={styles.AboutMe__username}>
                        <span className={styles.AboutMe__username__username}>{user.username}</span>
                    </div>

                    {!isMobile && !isOwner && likeUser}
                    {!!user.isAmbassadorOfBrandId && ambassadorPanel}
                </div>
            );
        }

        return (
            <>
                <div className={styles.AboutMe}>
                    {avatar}
                    <div className={styles.AboutMe__username}>
                        <span className={styles.AboutMe__username__username}>{user.username}</span>
                        {userAge && (
                            <span className={styles.AboutMe__username__age}>
                                {interpolate(number_YEARS, { count: userAge, number: userAge })}
                            </span>
                        )}
                    </div>

                    {(user.isVerifiedOrganization ||
                        userIsInactive ||
                        user.isOfficialProfile ||
                        user.isCommercialAmbassador) && (
                        <div className={styles.AboutMe__badges}>
                            {user.isVerifiedOrganization && (
                                <Badge
                                    type={BadgeType.VerifiedOrganization}
                                    className={styles.AboutMe__badge}
                                />
                            )}
                            {userIsInactive && (
                                <Badge
                                    type={BadgeType.InactiveUser}
                                    className={styles.AboutMe__badge}
                                />
                            )}
                            {user.isOfficialProfile && (
                                <Badge
                                    type={BadgeType.OfficialProfile}
                                    className={styles.AboutMe__badge}
                                />
                            )}
                            {user.isCommercialAmbassador && (
                                <Badge
                                    type={BadgeType.CommercialAmbassador}
                                    className={styles.AboutMe__badge}
                                />
                            )}
                        </div>
                    )}

                    <div className={styles.AboutMe__stats}>
                        {isMobile && moreAboutMeHasContent ? (
                            <>
                                <Link
                                    to={url(blogsProfileMoreUrl, { username: user.username })}
                                    className={styles.AboutMe__stats__following}
                                >
                                    {interpolate(ABOUT_ME_FOLLOWING_count, {
                                        count: following.count,
                                    })}
                                </Link>
                                <Link
                                    to={url(blogsProfileMoreUrl, { username: user.username })}
                                    className={styles.AboutMe__stats__followers}
                                >
                                    {interpolate(ABOUT_ME_FOLLOWERS_count, {
                                        count: followers.count,
                                    })}
                                </Link>
                                <br />
                                <Link
                                    to={url(blogsProfileMoreUrl, { username: user.username })}
                                    className={styles.AboutMe__stats__groups}
                                >
                                    {interpolate(ABOUT_ME_GROUPS_count, {
                                        count: memberOfGroups.count,
                                    })}
                                </Link>
                            </>
                        ) : (
                            <>
                                <span className={styles.AboutMe__stats__following}>
                                    {interpolate(ABOUT_ME_FOLLOWING_count, {
                                        count: following.count,
                                    })}
                                </span>
                                <span className={styles.AboutMe__stats__followers}>
                                    {interpolate(ABOUT_ME_FOLLOWERS_count, {
                                        count: followers.count,
                                    })}
                                </span>
                                <br />
                                <span className={styles.AboutMe__stats__groups}>
                                    {interpolate(ABOUT_ME_GROUPS_count, {
                                        count: memberOfGroups.count,
                                    })}
                                </span>
                            </>
                        )}
                        {userStats.marketProductsCount != null && (
                            <Link
                                to={url(isOwner ? bazaarMyBazaarUrl : bazaarSellerBazaarUrl, {
                                    username: user.username,
                                })}
                            >
                                {interpolate(ABOUT_ME_PRODUCTS_count, {
                                    count: userStats.marketProductsCount,
                                })}
                            </Link>
                        )}
                    </div>

                    {!isMobile && !isOwner && likeUser}
                    {!isMobile && !isOwner && (
                        <div className={styles.AboutMe__sendMessage}>
                            <Btn
                                layout={BtnLayout.IconLabelCount}
                                type={BtnType.Link}
                                icon={faEnvelope}
                                label={SEND_INTERNAL_POST}
                                link={url(mailComposeUrl, {}, { to: user.username })}
                            />
                        </div>
                    )}

                    {(userRegisteredOn || userResidence) && (
                        <div className={styles.AboutMe__registrationAndResidence}>
                            {userResidence && (
                                <span className={styles.AboutMe__registrationAndResidence__place}>
                                    {userResidenceCountyId ? (
                                        <Link
                                            to={url(
                                                usersDirectoryUrl,
                                                {},
                                                { county: userResidenceCountyId },
                                            )}
                                        >
                                            {userResidence}
                                        </Link>
                                    ) : (
                                        userResidence
                                    )}
                                </span>
                            )}

                            {userRegisteredOn && (
                                <span
                                    className={
                                        styles.AboutMe__registrationAndResidence__registration
                                    }
                                >
                                    {interpolate(PROFILE_REGISTERED_ON_date, {
                                        date: userRegisteredOn,
                                    })}
                                </span>
                            )}
                        </div>
                    )}

                    {isAdmin && (
                        <div className={styles.AboutMe__profileAdminTools}>
                            <ProfileAdminTools
                                user={user}
                                onlineStatus={userOnlineStatus}
                                onDeleteAvatarByAdmin={onDeleteAvatarByAdmin}
                            />
                        </div>
                    )}

                    {pregnancyDaysCounter && pregnancyWeekNumber && (
                        <div className={styles.AboutMe__pregnancyState}>
                            <Interpolate
                                i18nKey={PROFILE_SUMMARY__name_IS_in_num_pregnancy_week}
                                name={capFirst(user.username)}
                                in_num_pregnancy_week={interpolate(
                                    PROFILE_SUMMARY__IN_num_PREGNANCY_WEEK,
                                    {
                                        count: pregnancyWeekNumber,
                                        num: pregnancyWeekNumber,
                                    },
                                )}
                            />
                            .{" "}
                            {isOwner ||
                                (isBlogsAdmin && (
                                    <Link to={url(settingsProfileUrl, { username: user.username })}>
                                        {CHANGE}
                                    </Link>
                                ))}
                            <br />
                            {BEFORE_BIRTH_1}{" "}
                            <b>
                                {interpolate(number_DAYS, {
                                    count: pregnancyDaysCounter,
                                    number: pregnancyDaysCounter,
                                })}
                            </b>{" "}
                            {BEFORE_BIRTH_2}
                        </div>
                    )}

                    {userAboutMeText?.trim() && (
                        <ExpandableClamp expandLabel={ABOUT_ME_MORE_ABOUT_ME} clamp={3}>
                            <div className={styles.AboutMe__about}>{userAboutMeText}</div>
                        </ExpandableClamp>
                    )}

                    {isOwner && (
                        <div className={styles.AboutMe__modify}>
                            <Btn
                                label={ABOUT_ME_CHANGE_PROFILE}
                                link={url(settingsProfileUrl, { username: user.username })}
                                type={BtnType.BlueOutline}
                            />
                        </div>
                    )}

                    {!!user.isAmbassadorOfBrandId && ambassadorPanel}
                </div>
            </>
        );
    }

    private handleLikeUser = () => {
        const { profileDetailData, onSetLikeUser } = this.props;
        if (!!profileDetailData && profileDetailData.user) {
            onSetLikeUser(profileDetailData.user.username, true);
        }
    };

    private handleUnLikeUser = () => {
        const { profileDetailData, onSetLikeUser } = this.props;
        if (!!profileDetailData && profileDetailData.user) {
            onSetLikeUser(profileDetailData.user.username, false);
        }
    };
}

interface PlaceholderOwnProps {
    isMobile: boolean;
    username: string;
}

export const AboutMePlaceholder = React.memo<PlaceholderOwnProps>(({ isMobile, username }) => {
    return (
        <div className={styles.AboutMe}>
            <div className={styles.AboutMe__avatar}>
                <LoadingPlaceholder width={isMobile ? 150 : 200} height={isMobile ? 150 : 200} />
            </div>

            <div className={styles.AboutMe__username}>
                <span className={styles.AboutMe__username__username}>{username}</span>
            </div>

            <div className={styles.AboutMe__stats}>
                <span className={styles.AboutMe__stats__following}>
                    <LoadingPlaceholder width={118} />
                </span>
                <span className={styles.AboutMe__stats__followers}>
                    <LoadingPlaceholder width={118} />
                </span>
                <br />
                <span className={styles.AboutMe__stats__groups}>
                    <LoadingPlaceholder width={55} />
                </span>
                <span className={styles.AboutMe__stats__products}>
                    <LoadingPlaceholder width={100} />
                </span>
            </div>

            <div className={styles.AboutMe__like}>
                <LoadingPlaceholder width={164} height={34} />
            </div>

            <LoadingPlaceholder
                width="100%"
                height={isMobile ? 34 : 500}
                style={{ marginTop: 10 }}
            />
        </div>
    );
});

AboutMePlaceholder.displayName = "AboutMePlaceholder";
